/** @file Unequips a mounted item: its slots go back to the socket's parent bone and it stays in place. */
import { z } from 'zod';
import type { Draft } from 'immer';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { rehomeSlots } from './rehomeSlots';
import { slotAttachments } from './slotAttachments';
import { slotsHolding } from './slotsHolding';

/**
 * The item plus every attachment mounted on the same socket that shares a slot with it (transitively), and every slot
 * holding one of them: they leave the socket bone together, or checkDocMounts would reject the co-mounted ones.
 */
function leaving(doc: Doc, item: Id, socketId: Id): { atts: Set<Id>; slots: Set<Id> } {
  const atts = new Set([item]);
  const slots = new Set<Id>();
  for (const att of atts) {
    for (const slot of slotsHolding(doc, att)) {
      slots.add(slot);
      for (const other of slotAttachments(doc, slot).filter((o) => doc.attachments[o]?.mount?.socket === socketId)) atts.add(other);
    }
  }
  return { atts, slots };
}

/** Deletes the mount of `id`; an attachment other than the item itself is named in an INFO at `item`. */
function unmount(draft: Draft<Doc>, ctx: CommandContext, id: Id, item: Id): void {
  const att = draft.attachments[id];
  if (att === undefined) return;
  delete att.mount;
  if (id === item) return;
  const message = `attachment "${att.name}" shares a slot with the item and was mounted on the same socket; it was unequipped too`;
  ctx.warnings.push({ code: 'INFO', message, params: { attachment: att.name }, path: 'item' });
}

/** socket.unequip: the item (and attachments co-mounted in its slots) leave the socket bone in place; the socket stays. */
export const command = defineCommand({
  name: 'socket.unequip',
  description:
    "Unequip a mounted item attachment: its slots move from the socket bone back to the socket's parent bone, its transform is recomposed so it stays in place, and the mount is removed (the socket stays); attachments mounted on the same socket in those slots are unequipped with it (INFO).",
  input: z.object({ item: z.string().min(1) }).strict(),
  apply(draft, input, ctx) {
    const attId = ctx.resolve('attachment', input.item);
    const att = draft.attachments[attId] ?? throwError('NOT_FOUND', { kind: 'attachment', ref: attId });
    const socketId = att.mount?.socket;
    const socket = socketId === undefined ? undefined : draft.sockets[socketId];
    if (socketId === undefined || socket === undefined) throwError('VALIDATION', undefined, { path: 'item', hint: `attachment ${att.name} is not equipped` });
    const { atts, slots } = leaving(draft, attId, socketId);
    rehomeSlots(draft, socket, [...slots]);
    for (const id of atts) unmount(draft, ctx, id, attId);
    draft.meta.updatedAt = ctx.now;
  },
});
